import { useState } from 'react'
import { getScanTypeDisplayName } from '@/utils/displayNames'
import { generateReportPDF } from '@/utils/pdfGenerator'
import { fetchWithRefresh } from '@/utils/fetchWithRefresh'

export interface SOC2Control {
  id: string
  name: string
  description: string
  category: string
  score: number
  vulnerability_count: number
  severity_breakdown: {
    critical: number
    high: number
    medium: number
    low: number
    info: number
  }
}

export interface SOC2Compliance {
  overall_technical_compliance_score: number
  technical_controls: Record<string, SOC2Control>
  total_vulnerabilities: number
  controls_scanned: number
  disclaimer: string
  organizational_controls: Array<{
    id: string
    name: string
    requires: string
    examples: string
  }>
  scan_coverage: {
    covered_controls: string[]
    not_covered_controls: string[]
  }
}

export interface ScanWarning {
  severity: 'high' | 'medium' | 'low'
  tool: string
  type: 'timeout' | 'error'
  message: string
  impact: string
  recommendation: string
  duration: number
}

export interface ReportData {
  id: string
  target: string
  scanType: string
  securityScore: number
  vulnerabilities: any[]
  scanDetails: {
    startTime: string
    endTime: string
    duration: string
    toolsUsed: string[]
  }
  soc2Compliance?: SOC2Compliance
  warnings?: ScanWarning[]
  scan_completeness?: string
}

export function useReportActions(toast?: (options: {
  title: string
  description?: string
  variant?: "default" | "success" | "destructive" | "confirmation"
}) => void) {
  const [selectedReport, setSelectedReport] = useState<string | null>(null)
  const [reportData, setReportData] = useState<ReportData | null>(null)

  const handleViewReport = async (reportId: string) => {
    try {
      // Fetch detailed scan results
      const response = await fetchWithRefresh(`/api/scan/results/${reportId}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.ok) {
        const data = await response.json()
        setReportData({
          id: reportId,
          target: data.url,
          scanType: getScanTypeDisplayName(data.scan_type || 'security'),
          securityScore: data.security_score || 0,
          vulnerabilities: (data.vulnerabilities || []).map((vuln: any) => ({
            ...vuln,
            recommendation: vuln.remediation // Map remediation to recommendation
          })),
          scanDetails: {
            startTime: data.started_at || data.created_at,
            endTime: data.completed_at,
            duration: data.scan_duration_seconds
              ? `${Math.round(data.scan_duration_seconds / 60)} minutes`
              : data.completed_at && data.started_at
                ? `${Math.round((new Date(data.completed_at).getTime() - new Date(data.started_at).getTime()) / 60000)} minutes`
                : 'N/A',
            toolsUsed: data.tools_executed || []
          },
          soc2Compliance: data.soc2_compliance,
          warnings: data.warnings,
          scan_completeness: data.scan_completeness
        })
        setSelectedReport(reportId)
      } else {
        console.error('Failed to fetch report details')
        toast?.({
          title: "Error",
          description: "Failed to fetch report details",
          variant: "destructive"
        })
      }
    } catch (error) {
      console.error('Error fetching report details:', error)
      toast?.({
        title: "Error",
        description: "Failed to load report details",
        variant: "destructive"
      })
    }
  }

  const handleDownloadReport = async (reportId: string) => {
    // Show progress toast
    toast?.({
      title: "Generating Report",
      description: "Creating PDF report with your branding...",
      variant: "default"
    })

    try {
      // If we already have the report data, use it directly
      if (reportData && reportData.id === reportId) {
        // Fetch branding data
        const brandingResponse = await fetchWithRefresh('/api/agency-branding/config', {
          credentials: 'include',
        }).catch(() => null)

        let branding = null
        if (brandingResponse?.ok) {
          branding = await brandingResponse.json()
        }

        // Generate PDF using client-side generation
        await generateReportPDF(reportData, branding)

        // Show success toast
        toast?.({
          title: "Report Generated",
          description: "PDF report has been generated successfully",
          variant: "success"
        })
        return
      }

      // If we don't have the report data, fetch it first
      const response = await fetchWithRefresh(`/api/scan/results/${reportId}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch report data: ${response.status}`)
      }

      const data = await response.json()
      const reportDataForPDF = {
        id: reportId,
        target: data.url,
        scanType: getScanTypeDisplayName(data.scan_type || 'security'),
        securityScore: data.security_score || 0,
        vulnerabilities: (data.vulnerabilities || []).map((vuln: any) => ({
          ...vuln,
          recommendation: vuln.remediation // Map remediation to recommendation
        })),
        scanDetails: {
          startTime: data.started_at || data.created_at,
          endTime: data.completed_at,
          duration: data.scan_duration_seconds
            ? `${Math.round(data.scan_duration_seconds / 60)} minutes`
            : data.completed_at && data.started_at
              ? `${Math.round((new Date(data.completed_at).getTime() - new Date(data.started_at).getTime()) / 60000)} minutes`
              : 'N/A',
          toolsUsed: data.tools_executed || []
        },
        soc2Compliance: data.soc2_compliance,
        warnings: data.warnings,
        scan_completeness: data.scan_completeness
      }

      // Fetch branding data
      const brandingResponse = await fetchWithRefresh('/api/agency-branding/config', {
        credentials: 'include',
      }).catch(() => null)

      let branding = null
      if (brandingResponse?.ok) {
        branding = await brandingResponse.json()
      }

      // Generate PDF using client-side generation
      await generateReportPDF(reportDataForPDF, branding)

      // Show success toast
      toast?.({
        title: "Report Generated",
        description: "PDF report has been generated successfully",
        variant: "success"
      })

    } catch (error) {
      console.error('Error downloading report:', error)
      toast?.({
        title: "Download Failed",
        description: `Failed to download report: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      })
    }
  }

  const handleClosePreview = () => {
    setSelectedReport(null)
    setReportData(null)
  }

  return {
    selectedReport,
    reportData,
    handleViewReport,
    handleDownloadReport,
    handleClosePreview
  }
}